import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { TabPanel, TabPanels, Tabs } from '@reach/tabs';
import { Tab, TabList, Button } from '@getsynapse/design-system';
import { SETTINGS_SECTIONS, PATHS } from 'utils/constants';
import ProjectSettings from './ProjectSettings/ProjectSettings';
import IntakeSettings from './IntakeSettings/IntakeSettings';
import Fields from './Fields/Fields';
import { useHistory } from 'react-router-dom';
import { fetchBudgetSettings } from 'state/BudgetList/budgetListSlice';
import FinancialSettings from './Finance/FinancialSettings';
import usePermissions from 'Hooks/usePermissions';
import { PERMISSIONS } from 'utils/permissions';

const Configurations = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { budgetManagement = false } = useFlags();
  const [
    canAccessIntakeSettings,
    canAccessProjectSettings,
    canAccessFieldsSettings,
    canAccessFinancialSettings,
  ] = usePermissions([
    PERMISSIONS.ACCESS_INTAKE_SETTINGS,
    PERMISSIONS.ACCESS_PROJECT_SETTINGS,
    PERMISSIONS.ACCESS_FIELDS_SETTINGS,
    PERMISSIONS.ACCESS_FINANCIAL_SETTINGS,
  ]);

  const showFinancialTab = budgetManagement && canAccessFinancialSettings;

  const indexParam = new URLSearchParams(window.location.search).get('section');
  const sectionMapping = {
    [SETTINGS_SECTIONS.INTAKE]: 0,
    [SETTINGS_SECTIONS.PROJECTS]: 1,
    [SETTINGS_SECTIONS.FIELDS]: 2,
    [SETTINGS_SECTIONS.FINANCIAL]: 3,
  };

  useEffect(() => {
    if (showFinancialTab) {
      dispatch(fetchBudgetSettings());
    }
  }, [dispatch, showFinancialTab]);

  const [activeIndex, setActiveIndex] = useState<number>(
    indexParam ? sectionMapping[indexParam] : 0
  );

  return (
    <Tabs
      className='min-h-full flex flex-col'
      index={activeIndex}
      onChange={(index: number) => {
        setActiveIndex(index);
        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}?section=${
            Object.keys(sectionMapping)[index]
          }`
        );
      }}
      data-cy='platform-settings-tabs'
    >
      <div className='flex justify-between'>
        <TabList className='max-w-3xl mb-4 flex-1'>
          {canAccessIntakeSettings && (
            <Tab index={0} data-cy='platform-request'>
              {intl.get('INTAKE')}
            </Tab>
          )}

          {canAccessProjectSettings && (
            <Tab index={1} data-cy='platform-settings__projects-tab'>
              {intl.get('ENTITIES.PROJECT', { num: 2 })}
            </Tab>
          )}

          {canAccessFieldsSettings && (
            <Tab index={2} data-cy='platform-settings__fields-tab'>
              {intl.get('ENTITIES.FIELDS', { num: 2 })}
            </Tab>
          )}

          {showFinancialTab && (
            <Tab index={3} data-cy='platform-settings__financial-tab'>
              {intl.get('ENTITIES.FINANCE', { num: 1 })}
            </Tab>
          )}
        </TabList>
        {canAccessFieldsSettings && activeIndex === 2 && (
          <Button
            onClick={() => history.push(PATHS.NEW_CUSTOM_FIELD_PAGE)}
            data-cy='add-fields-button'
            className='h-8'
          >
            {intl.get('SETTINGS_PAGE.FIELDS_PAGE.BUTTON_TITLE')}
          </Button>
        )}
      </div>
      <TabPanels className='flex-grow flex flex-col pb-4 max-h-details-without-bar'>
        <TabPanel key={0} className='flex-grow bg-neutral-white rounded pt-6'>
          <IntakeSettings />
        </TabPanel>

        <TabPanel key={1} className='flex-grow bg-neutral-white rounded'>
          <ProjectSettings />
        </TabPanel>

        <TabPanel key={2} className='flex-grow bg-neutral-white rounded'>
          <Fields />
        </TabPanel>

        <TabPanel key={3} className='flex-grow bg-neutral-white rounded'>
          <FinancialSettings />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default Configurations;
